// src/components/common/ReviewCard.js
// 34차: 신규 — 리뷰 카드 공통 컴포넌트
// 36차: 매장 답변 표시 + storeAdmin 답변 입력 UI 추가
// 37차: 리뷰 댓글 기능 추가 (reviews/{id}/comments 서브컬렉션)
// 51차: 색 theme.js 단일화 — 자체 색 정의 제거, COLORS 참조 / 초록 통일
// 72차: replyTemplate prop 추가 — 답변 미작성 리뷰의 입력창에 매장/지점별 기본 인사말을
//       미리 채워줌 (ReviewList까지 관통, StoreAdminScreen/ReplyToReviewScreen에서 사용)
// 72차: 매장 관리자(adminUid 있을 때)에게 "🚩 신고" 버튼 노출 — reportReview()로
//       reports 컬렉션에 기록, 기존 SuperAdmin 신고 관리 화면을 그대로 재사용
// 74차: 작성자 닉네임 옆 [크루명] 태그 표시 (review.reviewerCrewName 스냅샷)
// 74차: "댓글 달기" 토글 버튼 터치 영역이 너무 작아(패딩 4/2) 누르기 힘들던 것을
//       패딩 확대 + hitSlop으로 개선 (marginHorizontal 음수로 시각적 위치는 유지)

import React, { useState, useEffect } from 'react';
import { showAlert } from './AppAlert';
import {
  View, Text, TouchableOpacity, StyleSheet,
  ActivityIndicator, TextInput, ScrollView, Pressable,
} from 'react-native';
// 78차 37차: 댓글 아바타/리뷰 사진 캐싱 없이 매번 재다운로드되던 것 — expo-image로 교체
// (디스크 캐시 자동, 여러 화면에서 재사용되는 공용 컴포넌트라 효과가 큼)
import { Image } from 'expo-image';
import {
  collection, addDoc, getDocs, deleteDoc, doc,
  query, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { replyToReview, deleteReply, reportReview } from '../../services/reviewService';
import { blockUser } from '../../services/blockService';
import { useAuth } from '../../contexts/AuthContext';
import { COLORS } from '../../constants/theme';
import {
  MenuStoreIcon, IconLabel, StarIcon, CheckmarkIcon, SpoilerWarningIcon,
  MenuInquiryIcon, EscapeRecordSuccessIcon, EscapeRecordFailIcon,
  DifficultyRatingIcon, ProductionRatingIcon, NoEntryIcon, ReportFlagIcon,
} from './Icons';

const GOLD       = COLORS.gold;
const SURFACE    = COLORS.surface;
const SURFACE2   = COLORS.surface2;
const SURFACE3   = '#0f0f1a';
const BORDER     = COLORS.border;
const TEXT       = COLORS.text;
const TEXT_SEC   = COLORS.textSec;
const TEXT_MUTED = COLORS.textMuted;
const RED        = COLORS.red;
const GREEN      = COLORS.green;
const ON_GOLD    = COLORS.onGold;

// ── 별점 ──────────────────────────────────────────────────────────────────────
export function StarRating({ rating, size = 14 }) {
  return (
    <View style={{ flexDirection: 'row', gap: 2 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <StarIcon key={i} size={size} filled={i <= rating} color={GOLD} emptyColor="#333" />
      ))}
    </View>
  );
}

// ── 인증 배지 ─────────────────────────────────────────────────────────────────
export function VerifiedBadge({ verified }) {
  if (!verified) return null;
  return (
    <View style={r.verifiedBadge}>
      <IconLabel Icon={CheckmarkIcon} size={11} color={r.verifiedTxt.color} textStyle={r.verifiedTxt}>인증</IconLabel>
    </View>
  );
}

// ── 스포일러 블러 오버레이 ────────────────────────────────────────────────────
export function SpoilerText({ text, style }) {
  const [revealed, setRevealed] = useState(false);
  if (!text) return null;
  return (
    <Pressable onPress={() => setRevealed(v => !v)} style={{ marginBottom: 10 }}>
      <Text style={[r_spoiler.text, style]}>{text}</Text>
      {!revealed && (
        <View style={r_spoiler.overlay}>
          <View style={r_spoiler.pill}>
            <SpoilerWarningIcon size={r_spoiler.pillIcon.fontSize || 14} color={GOLD} />
            <Text style={r_spoiler.pillTxt}>스포일러 포함 · 탭하여 보기</Text>
          </View>
        </View>
      )}
    </Pressable>
  );
}

const r_spoiler = StyleSheet.create({
  text:    { color: TEXT_SEC, fontSize: 13, lineHeight: 20 },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(15,15,26,0.92)',
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pill: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: 'rgba(212,168,67,0.15)', borderRadius: 20,
    borderWidth: 1, borderColor: 'rgba(212,168,67,0.4)',
    paddingHorizontal: 14, paddingVertical: 7,
  },
  pillIcon: { fontSize: 13 },
  pillTxt:  { color: GOLD, fontSize: 12, fontWeight: '700' },
});


function ReviewComments({ reviewId, currentUser, currentProfile, showComments, onToggle }) {
  const [comments,     setComments]     = useState([]);
  const [loaded,       setLoaded]       = useState(false);
  const [inputText,    setInputText]    = useState('');
  const [submitting,   setSubmitting]   = useState(false);

  useEffect(() => {
    if (!showComments || loaded) return;
    getDocs(query(
      collection(db, 'reviews', reviewId, 'comments'),
      orderBy('createdAt', 'asc'),
    ))
      .then(snap => setComments(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [showComments, reviewId]);

  // 79차: [버그 수정] ReviewList(위)는 차단한 유저의 리뷰를 이미 걸러내는데, 리뷰
  // 하나에 달리는 이 댓글 스레드만 blockedUsers 필터링이 빠져 있었음(RecruitDetailScreen.js
  // 댓글/대댓글에서 같은 유형을 발견/수정한 전례와 동일한 사각지대, 리뷰/공지 화면
  // 재정독 fork로 발견) — 차단해도 그 사람의 댓글이 그대로 보이고 있었음.
  const blockedUsers = currentProfile?.blockedUsers || [];
  const visibleComments = blockedUsers.length
    ? comments.filter(c => !blockedUsers.includes(c.uid))
    : comments;

  const handleAdd = async () => {
    const text = inputText.trim();
    if (!text || !currentUser) return;
    setSubmitting(true);
    try {
      const ref = await addDoc(collection(db, 'reviews', reviewId, 'comments'), {
        uid:        currentUser.uid,
        authorName: currentProfile?.nickname || currentProfile?.displayName || '익명',
        photoURL:   currentProfile?.photoURL || currentUser.photoURL || null,
        text,
        createdAt:  serverTimestamp(),
      });
      setComments(prev => [...prev, {
        id: ref.id, uid: currentUser.uid,
        authorName: currentProfile?.nickname || '익명',
        photoURL:   currentProfile?.photoURL || null,
        text, createdAt: null,
      }]);
      setInputText('');
    } catch {
      showAlert('오류', '댓글 작성에 실패했습니다.');
    } finally { setSubmitting(false); }
  };

  const handleDelete = (commentId) => {
    showAlert('댓글 삭제', '댓글을 삭제하시겠습니까?', [
      { text: '취소', style: 'cancel' },
      { text: '삭제', style: 'destructive', onPress: async () => {
        try {
          await deleteDoc(doc(db, 'reviews', reviewId, 'comments', commentId));
          setComments(prev => prev.filter(c => c.id !== commentId));
        } catch { showAlert('오류', '삭제에 실패했습니다.'); }
      }},
    ]);
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return `${d.getMonth()+1}/${d.getDate()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
    } catch { return ''; }
  };

  return (
    <View style={r.commentSection}>
      {/* 댓글 토글 버튼 */}
      <TouchableOpacity
        style={r.commentToggleBtn}
        onPress={onToggle}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      >
        {showComments
          ? <Text style={r.commentToggleTxt}>▲ 댓글 닫기</Text>
          : <IconLabel Icon={MenuInquiryIcon} size={12} color={r.commentToggleTxt.color} textStyle={r.commentToggleTxt}>
              {`댓글 ${loaded && visibleComments.length > 0 ? `(${visibleComments.length})` : '달기'}`}
            </IconLabel>}
      </TouchableOpacity>

      {showComments && (
        <View style={r.commentBody}>
          {/* 댓글 목록 */}
          {!loaded ? (
            <ActivityIndicator color={GOLD} size="small" style={{ marginVertical: 8 }} />
          ) : visibleComments.length === 0 ? (
            <Text style={r.commentEmpty}>첫 댓글을 남겨보세요!</Text>
          ) : (
            visibleComments.map(c => {
              const isMine = c.uid === currentUser?.uid;
              const avatarLetter = (c.authorName || '?')[0].toUpperCase();
              return (
                <View key={c.id} style={[r.commentItem, isMine && r.commentItemMine]}>
                  <View style={r.commentMeta}>
                    {c.photoURL ? (
                      <Image source={{ uri: c.photoURL }} style={[r.commentAvatar, isMine && { borderColor: GOLD + '60' }]} />
                    ) : (
                      <View style={[r.commentAvatarFallback, isMine && { backgroundColor: GOLD + '20', borderColor: GOLD + '40' }]}>
                        <Text style={[r.commentAvatarTxt, isMine && { color: GOLD }]}>{avatarLetter}</Text>
                      </View>
                    )}
                    <Text style={[r.commentAuthor, isMine && { color: GOLD }]}>
                      {isMine ? '나' : c.authorName || '익명'}
                    </Text>
                    <View style={{ flex: 1 }} />
                    {c.createdAt && (
                      <Text style={r.commentTime}>{formatTime(c.createdAt)}</Text>
                    )}
                    {isMine && (
                      <TouchableOpacity onPress={() => handleDelete(c.id)} hitSlop={{ top:8, bottom:8, left:8, right:8 }}>
                        <Text style={{ color: RED, fontSize: 11, marginLeft: 6 }}>삭제</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                  <Text style={r.commentTxt}>{c.text}</Text>
                </View>
              );
            })
          )}

          {/* 댓글 입력 */}
          {currentUser ? (
            <View style={r.commentInputRow}>
              <TextInput
                style={r.commentInput}
                value={inputText}
                onChangeText={setInputText}
                placeholder="댓글을 입력하세요..."
                placeholderTextColor={TEXT_MUTED}
                multiline
                maxLength={300}
              />
              <TouchableOpacity
                style={[r.commentSendBtn, (!inputText.trim() || submitting) && { opacity: 0.4 }]}
                onPress={handleAdd}
                disabled={!inputText.trim() || submitting}>
                {submitting
                  ? <ActivityIndicator size="small" color={SURFACE} />
                  : <Text style={r.commentSendTxt}>전송</Text>}
              </TouchableOpacity>
            </View>
          ) : (
            <Text style={{ color: TEXT_MUTED, fontSize: 12, textAlign: 'center', paddingVertical: 8 }}>
              로그인 후 댓글을 달 수 있어요
            </Text>
          )}
        </View>
      )}
    </View>
  );
}

// ── 리뷰 카드 ─────────────────────────────────────────────────────────────────
// props:
//   review        : 리뷰 객체
//   adminUid      : 현재 로그인한 storeAdmin uid (있으면 답변 UI 표시)
//   showReply     : 답변 표시 여부 (기본 true)
//   onReplyChanged: 답변 변경 후 콜백 (optional)
//   currentUser   : 댓글용 로그인 유저 객체 (optional)
//   currentProfile: 댓글용 프로필 (optional)
// 72차: replyTemplate — 답변이 아직 없을 때 입력창에 미리 채워줄 기본 문구.
// 문자열 또는 (review) => string 함수 둘 다 받는다(지점별로 다른 서명이 필요해서).
export function ReviewCard({ review, adminUid, showReply = true, onReplyChanged, currentUser, currentProfile, replyTemplate }) {
  const [replyMode,      setReplyMode]      = useState(false);
  const template = typeof replyTemplate === 'function' ? replyTemplate(review) : replyTemplate;
  const [replyText,      setReplyText]      = useState(review.reply?.text || template || '');
  const [submitting,     setSubmitting]     = useState(false);
  const [showComments,   setShowComments]   = useState(false);

  const isAdmin   = !!adminUid;
  const hasReply  = !!review.reply?.text;
  const isEditing = replyMode;
  // 78차 42차: 리뷰 신고가 지금까지 매장관리자에게만 열려 있어(72차) 일반 회원은 다른
  // 사람이 쓴 부적절한 리뷰를 신고할 방법이 없었음(애플/구글 UGC 정책상 위험 요소로 지적됨) —
  // 로그인한 일반 회원에게도 열되, 본인 리뷰는 신고 못 하게 막음.
  const reporterUid = adminUid || currentUser?.uid;
  const canReport = !!reporterUid && reporterUid !== review.uid;

  const handleSubmitReply = async () => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    try {
      await replyToReview(review.id, adminUid, replyText);
      setReplyMode(false);
      onReplyChanged?.({ ...review, reply: { text: replyText, adminUid, repliedAt: new Date().toISOString() } });
    } catch {
      showAlert('오류', '답변 저장 중 오류가 발생했습니다.');
    } finally { setSubmitting(false); }
  };

  const handleDeleteReply = () => {
    showAlert('답변 삭제', '답변을 삭제하시겠어요?', [
      { text: '취소', style: 'cancel' },
      { text: '삭제', style: 'destructive', onPress: async () => {
        try {
          await deleteReply(review.id);
          setReplyText('');
          onReplyChanged?.({ ...review, reply: null });
        } catch { showAlert('오류', '삭제 중 오류가 발생했습니다.'); }
      }},
    ]);
  };

  // 72차: 리뷰 어뷰징 신고 — 매장 관리자용(adminUid 있을 때만 노출)
  const submitReviewReport = async (reason) => {
    try {
      await reportReview({
        reviewId: review.id,
        reporterUid,
        reporterName: currentProfile?.nickname || (isAdmin ? '매장관리자' : '회원'),
        review,
        reason,
      });
      showAlert('접수 완료', '신고가 접수됐어요. 운영팀이 검토합니다.');
    } catch (e) {
      if (e.message === 'ALREADY_REPORTED') showAlert('안내', '이미 신고한 리뷰예요.');
      else showAlert('오류', '신고 접수 중 오류가 발생했습니다.');
    }
  };
  const handleReportReview = () => {
    showAlert('리뷰 신고', '신고 사유를 선택해주세요.', [
      { text: '취소', style: 'cancel' },
      { text: '허위/조작 리뷰', onPress: () => submitReviewReport('허위/조작 리뷰') },
      { text: '욕설/비방', onPress: () => submitReviewReport('욕설/비방') },
      { text: '광고/스팸', onPress: () => submitReviewReport('광고/스팸') },
      { text: '기타', onPress: () => submitReviewReport('기타') },
    ]);
  };

  // 78차 42차: 일반 회원용 차단 — 매장관리자 컨텍스트(adminUid)에선 안 보여줌(모니터링용 신고와 별개)
  const { user: authedUser, refreshProfile } = useAuth();
  const canBlock = !isAdmin && !!authedUser?.uid && !!review.uid && authedUser.uid !== review.uid;
  const handleBlockReviewer = () => {
    showAlert('사용자 차단', `"${review.reviewer || '이 사용자'}"의 리뷰를 앞으로 보지 않아요.`, [
      { text: '취소', style: 'cancel' },
      { text: '차단', style: 'destructive', onPress: async () => {
        try {
          await blockUser(authedUser.uid, review.uid);
          await refreshProfile();
        } catch { showAlert('오류', '차단 처리 중 오류가 발생했습니다.'); }
      }},
    ]);
  };

  return (
    <View style={r.card}>
      {/* 작성자 + 별점 */}
      <View style={r.header}>
        <View style={r.authorRow}>
          <View style={r.avatar}>
            <Text style={r.avatarTxt}>{(review.reviewer || '익명')[0]}</Text>
          </View>
          <View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
              {!!review.reviewerCrewName && (
                <Text style={r.crewTag} numberOfLines={1}>[{review.reviewerCrewName}]</Text>
              )}
              <Text style={r.reviewer}>{review.reviewer || '익명'}</Text>
            </View>
            <Text style={r.date}>{review.createdAt ? review.createdAt.slice(0, 10) : ''}</Text>
          </View>
        </View>
        <View style={{ alignItems: 'flex-end', gap: 4 }}>
          <StarRating rating={review.rating || 0} />
          <VerifiedBadge verified={review.verified} />
          <View style={{ flexDirection: 'row', gap: 10 }}>
            {/* 72차: 매장 관리자용, 78차 42차: 일반 회원(본인 리뷰 제외)에게도 확장 */}
            {canReport && (
              <TouchableOpacity onPress={handleReportReview} hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}>
                <IconLabel Icon={ReportFlagIcon} size={11} color={TEXT_SEC} textStyle={{ color: TEXT_SEC, fontSize: 11 }}>신고</IconLabel>
              </TouchableOpacity>
            )}
            {/* 78차 42차: 일반 회원용 사용자 차단 */}
            {canBlock && (
              <TouchableOpacity onPress={handleBlockReviewer} hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}>
                <IconLabel Icon={NoEntryIcon} size={11} color={TEXT_SEC} textStyle={{ color: TEXT_SEC, fontSize: 11 }}>차단</IconLabel>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>

      {/* 탈출 결과 */}
      {review.escapeResult != null && (
        <View style={[r.resultBadge, review.escapeResult ? r.resultSuccess : r.resultFail]}>
          <IconLabel Icon={review.escapeResult ? EscapeRecordSuccessIcon : EscapeRecordFailIcon} size={11} color={r.resultBadgeTxt.color} textStyle={r.resultBadgeTxt}>{review.escapeResult ? '탈출 성공' : '탈출 실패'}</IconLabel>
        </View>
      )}

      {/* 항목별 점수 */}
      {review.subRatings && Object.values(review.subRatings).some(v => v > 0) && (
        <View style={r.subRatingsWrap}>
          {[
            { key: 'horror',     label: '👻 공포도' },
            { key: 'difficulty', label: '난이도', Icon: DifficultyRatingIcon },
            { key: 'production', label: '연출',   Icon: ProductionRatingIcon },
            { key: 'story',      label: '📖 스토리' },
          ].filter(({ key }) => review.subRatings[key] > 0).map(({ key, label, Icon }) => (
            <View key={key} style={r.subRatingItem}>
              {Icon
                ? <IconLabel Icon={Icon} size={12} color={r.subRatingLabel.color} textStyle={r.subRatingLabel}>{label}</IconLabel>
                : <Text style={r.subRatingLabel}>{label}</Text>}
              <View style={{ flexDirection: 'row', gap: 1 }}>
                {[1,2,3,4,5].map(n => (
                  <StarIcon key={n} size={11} filled={n <= review.subRatings[key]} color={GOLD} emptyColor="#2a2a3a" />
                ))}
              </View>
            </View>
          ))}
        </View>
      )}

      {/* 리뷰 내용 */}
      {(review.comment || review.content) ? (
        review.spoiler
          ? <SpoilerText text={review.comment || review.content} style={r.comment} />
          : <Text style={r.comment}>{review.comment || review.content}</Text>
      ) : null}

      {/* 리뷰 이미지 */}
      {(review.imageUris?.length > 0 || review.imageUrls?.length > 0) && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={r.imageRow}>
          {(review.imageUris || review.imageUrls || []).map((uri, i) => (
            <Image key={i} source={{ uri }} style={r.reviewImage} />
          ))}
        </ScrollView>
      )}

      {/* 매장 답변 표시 */}
      {showReply && hasReply && !isEditing && (
        <View style={r.replyBox}>
          <View style={r.replyHeader}>
            <IconLabel Icon={MenuStoreIcon} size={11} color={GOLD} textStyle={r.replyLabel}>매장 답변</IconLabel>
            {isAdmin && (
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <TouchableOpacity onPress={() => { setReplyText(review.reply.text); setReplyMode(true); }}>
                  <Text style={r.replyEditBtn}>수정</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={handleDeleteReply}>
                  <Text style={r.replyDeleteBtn}>삭제</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
          <Text style={r.replyText}>{review.reply.text}</Text>
          {review.reply.repliedAt && (
            <Text style={r.replyDate}>{review.reply.repliedAt.slice(0, 10)}</Text>
          )}
        </View>
      )}

      {/* storeAdmin 답변 입력/수정 */}
      {isAdmin && (isEditing || !hasReply) && (
        <View style={r.replyInputBox}>
          <TextInput
            style={r.replyInput}
            value={replyText}
            onChangeText={setReplyText}
            placeholder="매장 답변을 입력하세요..."
            placeholderTextColor={TEXT_MUTED}
            multiline
            maxLength={500}
          />
          <View style={r.replyInputBtnRow}>
            {isEditing && (
              <TouchableOpacity style={r.replyCancelBtn} onPress={() => setReplyMode(false)}>
                <Text style={r.replyCancelBtnTxt}>취소</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={[r.replySubmitBtn, (!replyText.trim() || submitting) && { opacity: 0.4 }]}
              onPress={handleSubmitReply}
              disabled={!replyText.trim() || submitting}>
              {submitting
                ? <ActivityIndicator size="small" color={ON_GOLD} />
                : <Text style={r.replySubmitBtnTxt}>{isEditing ? '수정 완료' : '답변 등록'}</Text>
              }
            </TouchableOpacity>
          </View>
          {!isEditing && !hasReply && (
            <TouchableOpacity onPress={() => setReplyMode(false)}>
              <Text style={{ color: TEXT_MUTED, fontSize: 11, textAlign: 'right', marginTop: 4 }}>닫기</Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* storeAdmin 답변 없을 때 답변 버튼 */}
      {isAdmin && !hasReply && !isEditing && (
        <TouchableOpacity style={r.replyOpenBtn} onPress={() => setReplyMode(true)}>
          <Text style={r.replyOpenBtnTxt}>+ 답변 달기</Text>
        </TouchableOpacity>
      )}

      {/* 리뷰 댓글 */}
      <ReviewComments
        reviewId={review.id}
        currentUser={currentUser}
        currentProfile={currentProfile}
        showComments={showComments}
        onToggle={() => setShowComments(v => !v)}
      />
    </View>
  );
}

// ── 리뷰 목록 ─────────────────────────────────────────────────────────────────
export function ReviewList({ reviews = [], loading = false, adminUid, onReplyChanged, currentUser, currentProfile, emptyText, replyTemplate }) {
  // 78차 42차: 차단한 유저의 리뷰는 피드에서만 숨김(매장관리자 화면은 모니터링 목적이라
  // adminUid가 있을 땐 필터링하지 않음 — 신고 대응을 위해 계속 보여야 함)
  const blockedUsers = currentProfile?.blockedUsers || [];
  const visibleReviews = (!adminUid && blockedUsers.length)
    ? reviews.filter(rv => !blockedUsers.includes(rv.uid))
    : reviews;

  if (loading) return <ActivityIndicator color={GOLD} style={{ marginVertical: 16 }} />;
  if (!visibleReviews.length) return (
    <View style={{ paddingVertical: 20, alignItems: 'center' }}>
      <Text style={{ color: TEXT_MUTED, fontSize: 13 }}>{emptyText || '아직 리뷰가 없습니다'}</Text>
    </View>
  );
  return (
    <View style={{ gap: 10 }}>
      {visibleReviews.map(rv => (
        <ReviewCard
          key={rv.id}
          review={rv}
          adminUid={adminUid}
          onReplyChanged={onReplyChanged}
          currentUser={currentUser}
          currentProfile={currentProfile}
          replyTemplate={replyTemplate}
        />
      ))}
    </View>
  );
}

const r = StyleSheet.create({
  card:          { backgroundColor: SURFACE2, borderRadius: 12, padding: 14,
                   borderWidth: 1, borderColor: BORDER },
  header:        { flexDirection: 'row', justifyContent: 'space-between',
                   alignItems: 'flex-start', marginBottom: 10 },
  authorRow:     { flexDirection: 'row', alignItems: 'center', gap: 8 },
  avatar:        { width: 32, height: 32, borderRadius: 16, backgroundColor: GOLD + '22',
                   borderWidth: 1, borderColor: GOLD + '44', alignItems: 'center', justifyContent: 'center' },
  avatarTxt:     { color: GOLD, fontWeight: '800', fontSize: 13 },
  reviewer:      { color: TEXT, fontWeight: '700', fontSize: 13 },
  crewTag:       { color: GOLD, fontWeight: '700', fontSize: 11 },
  date:          { color: TEXT_MUTED, fontSize: 11, marginTop: 2 },

  verifiedBadge: { backgroundColor: COLORS.greenBg, borderRadius: 4,
                   paddingHorizontal: 6, paddingVertical: 2, borderWidth: 1,
                   borderColor: COLORS.greenBorder, alignSelf: 'flex-end' },
  verifiedTxt:   { color: GREEN, fontSize: 10, fontWeight: '700' },

  resultBadge:   { alignSelf: 'flex-start', borderRadius: 6, paddingHorizontal: 8,
                   paddingVertical: 3, marginBottom: 8 },
  resultSuccess: { backgroundColor: COLORS.greenBg },
  resultFail:    { backgroundColor: COLORS.redBg },
  resultBadgeTxt:{ fontSize: 11, fontWeight: '700', color: TEXT_SEC },

  subRatingsWrap:{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 10,
                   backgroundColor: SURFACE3, borderRadius: 8, padding: 8 },
  subRatingItem: { flexDirection: 'row', alignItems: 'center', gap: 6, minWidth: '45%' },
  subRatingLabel:{ color: TEXT_MUTED, fontSize: 11, fontWeight: '600' },
  comment:       { color: TEXT_SEC, fontSize: 13, lineHeight: 20, marginBottom: 10 },
  imageRow:      { marginBottom: 10 },
  reviewImage:   { width: 80, height: 80, borderRadius: 8, marginRight: 8 },

  replyBox:      { backgroundColor: SURFACE, borderRadius: 8, padding: 10,
                   borderWidth: 1, borderColor: GOLD + '20', marginTop: 8 },
  replyHeader:   { flexDirection: 'row', justifyContent: 'space-between',
                   alignItems: 'center', marginBottom: 6 },
  replyLabel:    { color: GOLD, fontSize: 11, fontWeight: '800' },
  replyEditBtn:  { color: TEXT_MUTED, fontSize: 11, fontWeight: '600' },
  replyDeleteBtn:{ color: RED, fontSize: 11, fontWeight: '600' },
  replyText:     { color: TEXT_SEC, fontSize: 13, lineHeight: 19 },
  replyDate:     { color: TEXT_MUTED, fontSize: 10, marginTop: 4 },

  replyInputBox: { marginTop: 8, backgroundColor: SURFACE, borderRadius: 8,
                   padding: 10, borderWidth: 1, borderColor: GOLD + '20' },
  replyInput:    { color: TEXT, fontSize: 13, lineHeight: 20, minHeight: 60,
                   textAlignVertical: 'top' },
  replyInputBtnRow: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 8 },
  replyCancelBtn:{ borderRadius: 8, paddingVertical: 7, paddingHorizontal: 14,
                   borderWidth: 1, borderColor: BORDER },
  replyCancelBtnTxt: { color: TEXT_MUTED, fontSize: 12, fontWeight: '700' },
  replySubmitBtn:{ backgroundColor: GOLD, borderRadius: 8,
                   paddingVertical: 7, paddingHorizontal: 14 },
  replySubmitBtnTxt: { color: ON_GOLD, fontSize: 12, fontWeight: '800' },
  replyOpenBtn:  { marginTop: 8, alignSelf: 'flex-start', paddingHorizontal: 10,
                   paddingVertical: 5, borderRadius: 6, borderWidth: 1,
                   borderColor: GOLD + '30', backgroundColor: GOLD + '0A' },
  replyOpenBtnTxt: { color: GOLD, fontSize: 11, fontWeight: '700' },

  // 댓글 영역
  commentSection:    { marginTop: 10, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.05)', paddingTop: 8 },
  commentToggleBtn:  { alignSelf: 'flex-start', paddingVertical: 10, paddingHorizontal: 8, marginHorizontal: -8 },
  commentToggleTxt:  { color: TEXT_MUTED, fontSize: 12, fontWeight: '600' },
  commentBody:       { marginTop: 8, gap: 6 },
  commentEmpty:      { color: TEXT_MUTED, fontSize: 12, textAlign: 'center', paddingVertical: 8 },
  commentItem:       { backgroundColor: SURFACE3, borderRadius: 8, padding: 10,
                       borderWidth: 1, borderColor: 'rgba(255,255,255,0.05)' },
  commentItemMine:   { borderColor: GOLD + '30', backgroundColor: GOLD + '06' },
  commentMeta:       { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 4 },
  commentAvatar:     { width: 22, height: 22, borderRadius: 11, borderWidth: 1.5,
                       borderColor: 'rgba(255,255,255,0.2)' },
  commentAvatarFallback: { width: 22, height: 22, borderRadius: 11, borderWidth: 1.5,
                           borderColor: 'rgba(255,255,255,0.2)',
                           backgroundColor: 'rgba(255,255,255,0.08)',
                           alignItems: 'center', justifyContent: 'center' },
  commentAvatarTxt:  { fontSize: 10, fontWeight: '800', color: 'rgba(255,255,255,0.5)' },
  commentAuthor:     { color: TEXT_SEC, fontSize: 11, fontWeight: '700' },
  commentTime:       { color: TEXT_MUTED, fontSize: 10 },
  commentTxt:        { color: TEXT_SEC, fontSize: 13, lineHeight: 19 },
  commentInputRow:   { flexDirection: 'row', gap: 6, marginTop: 8, alignItems: 'flex-end' },
  commentInput:      { flex: 1, backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER,
                       borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8,
                       color: TEXT, fontSize: 13, maxHeight: 80 },
  commentSendBtn:    { backgroundColor: GOLD, borderRadius: 8, paddingHorizontal: 14,
                       paddingVertical: 10, justifyContent: 'center', alignItems: 'center' },
  commentSendTxt:    { color: ON_GOLD, fontWeight: '800', fontSize: 12 },
});